import React from 'react'
import { useIntl } from 'react-intl'

import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type {
  AccommodationItem,
  FlightItem,
  LETripItem,
} from 'tripPlanner/types/tripItem'
import { getOfferPagePrefilledStateParams } from 'tripPlanner/utils'
import { buildStandaloneFlightSearchURL } from 'tripPlanner/utils/bookmark/restoreCart'

interface Props {
  item: Extract<LETripItem, FlightItem | AccommodationItem>
  restoreCartUrl: string
}

function BookHotelFlight(props: Props) {
  const intl = useIntl()
  const { item, restoreCartUrl } = props

  const { closeModal, goBack } = useModalCallbacks()

  let unbundledText: string
  let unbundledURL: string

  if (item.type === 'ACCOMMODATION') {
    const { searchParams, hash } = getOfferPagePrefilledStateParams(item, {
      scrollToOptions: true,
    })
    const queryString = searchParams.toString()
    const URLSuffix = queryString ? `?${queryString}` : ''

    unbundledText = intl.formatMessage({
      id: '--lUtlkbhqrFovz_J-sz',
      defaultMessage: 'No, book hotel only',
      description: 'Button to book hotel without bundled flights in the flights flow',
    })
    unbundledURL = `/offer/hotel/${item.code}${URLSuffix}${hash}`
  } else {
    const data = structuredClone(item.savedItemData)
    delete data.hotelBundle

    unbundledText = intl.formatMessage({
      id: '6xCW1Y656g-DxxrtVxeJ',
      defaultMessage: 'No, book flights only',
      description: 'Button to book flights without bundled hotel in the flights flow',
    })
    unbundledURL = buildStandaloneFlightSearchURL(data)
  }

  return (
    <>
      <ModalHeader
        title={intl.formatMessage({
          id: 'QUHb9-Ei0_AOhA6UnChp',
          defaultMessage: 'Book hotel and flights package?',
          description: 'Modal title asking if user wants to book hotel and flights together',
        })}
        onBackButtonClick={goBack}
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        <ModalContent>
          <BodyText variant="large" as="p">
            {intl.formatMessage({
              id: '2OG1q3cWcJJcclBFBkp7',
              defaultMessage: 'You’ve added this order as a hotel and flights package.',
              description: 'Body text explaining the user added a hotel and flights bundle',
            })}
          </BodyText>
          <BodyText variant="large" as="p">
            {intl.formatMessage({
              id: 'DEhNCXRT9LGyE123_mDp',
              defaultMessage: 'Do you want to book these items together?',
              description: 'Question asking if user wants to book bundled items together',
            })}
          </BodyText>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: '0NRypM5vqRdbosQ5h-_c',
            defaultMessage: 'Yes, book hotel and flights',
            description: 'Button to confirm booking hotel and flights as a package',
          }),
          to: restoreCartUrl,
        }}
        secondaryActionProps={{
          children: unbundledText,
          to: unbundledURL,
        }}
      />
    </>
  )
}

export default BookHotelFlight
